import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiGet } from '../utils/apiClient';
import { isDoctypeAllowed, hasAnyDoctypeAllowed } from '../../shared/doctype-permissions';

// Per-user rights, read live from /api/me. The server is the authority — this
// only keeps the UI from offering actions that would be rejected.
//
// While loading, and on any error, we stay PERMISSIVE: every upload surface is
// already wrapped in OnlineOnly, and a transient /api/me hiccup must not flash
// a bogus "no permission" screen at users who do have the right.
const UserPermissionsContext = createContext();

const PERMISSIVE = { canUpload: true, allowedDoctypes: null };

export const UserPermissionsProvider = ({ children }) => {
  const [permissions, setPermissions] = useState(null); // null = not loaded yet
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await apiGet('/api/me');
        if (!cancelled) setPermissions(me?.permissions || PERMISSIVE);
      } catch (err) {
        if (!cancelled) { setError(err.message); setPermissions(PERMISSIVE); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const effective = permissions || PERMISSIVE;

  return (
    <UserPermissionsContext.Provider
      value={{
        permissions: effective,
        loading: permissions === null,
        error,
        canUpload: effective.canUpload !== false,
        allowedDoctypes: effective.allowedDoctypes,
        isDoctypeAllowed: (doctype) => isDoctypeAllowed(doctype, effective.allowedDoctypes),
        hasAnyDoctype: hasAnyDoctypeAllowed(effective.allowedDoctypes),
      }}
    >
      {children}
    </UserPermissionsContext.Provider>
  );
};

export const useUserPermissions = () => {
  const context = useContext(UserPermissionsContext);
  if (!context) {
    throw new Error('useUserPermissions must be used within a UserPermissionsProvider');
  }
  return context;
};
